/**
 * On-demand chunks (ADR 0042): a dialog, the file preview… is downloaded when it is first opened —
 * usually warmed a moment earlier on hover or while the browser is idle (lib/lazy.ts) — so the first
 * visit to a page carries only what the page shows.
 *
 * `LoadingNote`: what shows while such a chunk is on its way — a small status near the bottom of the
 * window. It does not take focus or block the page, and it fades in only after a short delay, so a
 * fast load shows nothing at all.
 *
 * `ChunkFailed`: the same place when the chunk could not be fetched (offline, a transient 5xx on
 * /assets/, a stale release that did not reload) — a Thai note with "ลองอีกครั้ง", instead of the
 * app-wide render-error screen. The page underneath stays as it is, with whatever was being edited.
 */
import { Component, Suspense, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError, lazyPage } from '../lib/lazy.js';

export function LoadingNote({ label = 'กำลังเปิด…' }: { label?: string }) {
  return (
    <div className="xc-lazy-note" role="status" aria-live="polite">
      <span className="xc-lazy-note-dot" aria-hidden="true" />
      {label}
    </div>
  );
}

export function ChunkFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="xc-lazy-note is-failed" role="alert">
      โหลดส่วนนี้ไม่สำเร็จ — อาจออฟไลน์อยู่ หรือแอปมีเวอร์ชันใหม่
      <button type="button" className="xc-btn xc-btn-sm" onClick={onRetry}>
        ลองอีกครั้ง
      </button>
    </div>
  );
}

/**
 * Catches a chunk that could not be fetched and offers a retry (the lazy wrapper is made anew after
 * a failure, lib/lazy.ts, so the retry downloads again). Any other error is a rendering bug and goes
 * on to the app's error boundary.
 */
class ChunkBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error)) console.warn('[xcreative] chunk could not be fetched', error.message, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    return <ChunkFailed onRetry={() => this.setState({ error: null })} />;
  }
}

/**
 * `lazyDialog(() => import('./ShareDialog.js'), 'ShareDialog')`: a drop-in stand-in for a dialog
 * component (same props) that downloads its chunk on first render, with `LoadingNote` meanwhile.
 * `.preload()` warms the chunk (pointer over the button that opens it).
 */
export function lazyDialog<M, K extends keyof M>(load: () => Promise<M>, name: K, label?: string): ComponentType<M[K] extends ComponentType<infer P> ? P : never> & { preload: () => void } {
  type P = M[K] extends ComponentType<infer Q> ? Q : never;
  const Lazy = lazyPage(load, name) as unknown as ComponentType<P> & { preload: () => void };
  function LazyDialog(props: P) {
    return (
      <ChunkBoundary>
        <Suspense fallback={<LoadingNote label={label} />}>
          <Lazy {...(props as P & object)} />
        </Suspense>
      </ChunkBoundary>
    );
  }
  LazyDialog.displayName = `Lazy(${String(name)})`;
  return Object.assign(LazyDialog, { preload: Lazy.preload });
}
